import 'rxjs/add/observable/from';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/mergeMap';
import type { Action as ActionType } from 'redux';
import { Observable } from 'rxjs/Observable';
import type { Location, RouterActionPush } from '@atlassian/react-resource-router';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../feature-flags.tsx';
import { QUERY_PARAM_IS_INSIGHTS_OPEN } from '../../model/routing/constants.tsx';
import type { CardId } from '../../model/types.tsx';
import { setPanelsUrl } from '../../services/routing/panels-transformer/index.tsx';
import { addSelectedIssueToUrl } from '../../services/routing/selected-issue-url-change/index.tsx';
import { setCheckboxSelectionMode } from '../../state/actions/cards/checkbox-selection-mode/index.tsx';
import {
	type CardClick,
	CARD_CLICK,
	cardSingleSelect,
	cardToggleSelection,
	cardMultiSelect,
	cardEmptySelection,
} from '../../state/actions/cards/selection/index.tsx';
import { closeDetailView, openDetailView } from '../../state/actions/detail-view/index.tsx';
import { closeInsightsPanel, closeViewSettingsPanel } from '../../state/actions/panels/index.tsx';
import {
	makeGetCardListIdByCardId,
	getListSelectionRelativeCard,
	getSelectedCards,
	makeCardKeyFromCardId,
	makeOrderedVisibleCardIdsForCardList,
	getOrderedCardListIds,
	getFocusedCard,
} from '../../state/selectors/cards/index.tsx';
import type { MiddlewareAPI, ActionsObservable, State } from '../../state/types.tsx';

const getCardIdsToListSelection = (state: State, relativeCardId: CardId, clickedCardId: CardId) => {
	const relativeCardListId = makeGetCardListIdByCardId(state)(relativeCardId);
	const clickedCardListId = makeGetCardListIdByCardId(state)(clickedCardId);

	if (!relativeCardListId || !clickedCardListId) return [];

	// selecting from the same card list
	if (relativeCardListId === clickedCardListId) {
		const cards = makeOrderedVisibleCardIdsForCardList(state)(relativeCardListId);
		const relativeCardIndex = cards.indexOf(relativeCardId);
		const clickedCardIndex = cards.indexOf(clickedCardId);
		const cardInBetween =
			clickedCardIndex > relativeCardIndex
				? cards.slice(relativeCardIndex, clickedCardIndex + 1)
				: cards.slice(clickedCardIndex, relativeCardIndex + 1).reverse();
		return cardInBetween;
	}

	// selecting from across card lists
	const orderedCardListIds = getOrderedCardListIds(state);
	const relativeCardListIndex = orderedCardListIds.indexOf(relativeCardListId);
	const clickedCardListIndex = orderedCardListIds.indexOf(clickedCardListId);
	const isSelectingCardListBelow = clickedCardListIndex > relativeCardListIndex;

	// getting all cards in card lists that are in between the relative and clicked cards
	const cardListsInBetween = orderedCardListIds.filter((_, index) => {
		if (isSelectingCardListBelow)
			return index > relativeCardListIndex && index < clickedCardListIndex;
		return index > clickedCardListIndex && index < relativeCardListIndex;
	});
	const cardsInBetweenCardList: CardId[] = cardListsInBetween.reduce(
		(result: CardId[], key: string) =>
			result.concat(makeOrderedVisibleCardIdsForCardList(state)(key)),
		[],
	);
	const orderedCardsInBetweenCardLists = isSelectingCardListBelow
		? cardsInBetweenCardList
		: cardsInBetweenCardList.reverse();

	// getting cards from where the relative card is based on the card order
	const relativeCardListCards = makeOrderedVisibleCardIdsForCardList(state)(relativeCardListId);
	const cardsToAddInRelativeCardList = isSelectingCardListBelow
		? relativeCardListCards.slice(
				relativeCardListCards.indexOf(relativeCardId),
				relativeCardListCards.length,
			)
		: relativeCardListCards.slice(0, relativeCardListCards.indexOf(relativeCardId) + 1).reverse();

	// getting cards from where the clicked card is based on the card order
	const clickedCardListCards = makeOrderedVisibleCardIdsForCardList(state)(clickedCardListId);
	const cardsToAddInClickedCardList = isSelectingCardListBelow
		? clickedCardListCards.slice(0, clickedCardListCards.indexOf(clickedCardId) + 1)
		: clickedCardListCards
				.slice(clickedCardListCards.indexOf(clickedCardId), clickedCardListCards.length)
				.reverse();

	return [
		...cardsToAddInRelativeCardList,
		...orderedCardsInBetweenCardLists,
		...cardsToAddInClickedCardList,
	];
};

const getLocationAfterCloseInsights = (location: Location) =>
	setPanelsUrl(QUERY_PARAM_IS_INSIGHTS_OPEN, false, location);

export const cardClickEpic =
	(getLocation: () => Location, push: RouterActionPush) =>
	(action$: ActionsObservable, store: MiddlewareAPI) =>
		action$.ofType(CARD_CLICK).mergeMap((action: CardClick) => {
			const state = store.getState();
			const { cardId, withCmd, withShift } = action.payload;
			const selectedCardIds = getSelectedCards(state);
			const focusedCard = getFocusedCard(state);
			const getCardKeyFromCardId = makeCardKeyFromCardId(state);

			if (!withShift && !withCmd && action.payload.externalAction) {
				const actions: ActionType[] = [cardSingleSelect(cardId)];

				actions.push(closeInsightsPanel(false));

				isViewSettingAsPanelExpEnabledWithNoExposure() && actions.push(closeViewSettingsPanel());

				actions.push(setCheckboxSelectionMode(!!action.payload.withCheckbox));

				push(
					addSelectedIssueToUrl(
						getCardKeyFromCardId(cardId),
						getLocationAfterCloseInsights(getLocation()),
					),
				);

				actions.push(openDetailView(cardId, action.payload.externalAction));

				action.payload.fireSelectionAnalytics?.({
					numSelectedCards: 1,
					issue_ids: [cardId],
				});

				return Observable.from(actions);
			}

			if (!withShift && !withCmd) {
				const actions: ActionType[] = [cardSingleSelect(cardId), openDetailView(cardId)];

				actions.push(closeInsightsPanel(false));

				isViewSettingAsPanelExpEnabledWithNoExposure() && actions.push(closeViewSettingsPanel());

				actions.push(setCheckboxSelectionMode(!!action.payload.withCheckbox));

				push(
					addSelectedIssueToUrl(
						getCardKeyFromCardId(cardId),
						getLocationAfterCloseInsights(getLocation()),
					),
				);

				action.payload.fireSelectionAnalytics?.({
					numSelectedCards: 1,
					issue_ids: [cardId],
				});

				return Observable.from(actions);
			}

			if (withShift && withCmd) {
				const relativeCardId = getListSelectionRelativeCard(state);
				if (!relativeCardId) {
					return Observable.empty<never>();
				}

				const cardsToBeSelected = getCardIdsToListSelection(state, relativeCardId, cardId);
				const relativeCardIndex = selectedCardIds.indexOf(relativeCardId);

				const newSelectionList = selectedCardIds
					.slice(0, relativeCardIndex === -1 ? selectedCardIds.length : relativeCardIndex) // clean last shift+cmd list selection
					.filter((cardIdInSelectedList) => !cardsToBeSelected.includes(cardIdInSelectedList)) // unique list
					.concat(cardsToBeSelected);

				const actions: ActionType[] = [cardMultiSelect(newSelectionList)];

				action.payload.fireSelectionAnalytics?.({
					numSelectedCards: newSelectionList.length,
					issue_ids: newSelectionList,
				});

				actions.push(setCheckboxSelectionMode(true));

				return Observable.from(actions);
			}

			if (withShift) {
				const relativeCardId = getListSelectionRelativeCard(state);
				if (!relativeCardId) {
					return Observable.empty<never>();
				}

				const cardsToBeSelected = getCardIdsToListSelection(
					state,
					relativeCardId,
					cardId,
				).reverse();
				const actions: ActionType[] = [cardMultiSelect(cardsToBeSelected)];

				action.payload.fireSelectionAnalytics?.({
					numSelectedCards: cardsToBeSelected.length,
					issue_ids: cardsToBeSelected,
				});

				focusedCard !== relativeCardId && actions.push(setCheckboxSelectionMode(true));

				return Observable.from(actions);
			}

			if (withCmd) {
				const actions: ActionType[] = [];

				if (selectedCardIds.length === 1 && cardId === selectedCardIds[0]) {
					actions.push(cardEmptySelection(), closeDetailView());
					actions.push(setCheckboxSelectionMode(false));
				} else {
					actions.push(cardToggleSelection(cardId));
					actions.push(setCheckboxSelectionMode(true));
				}
				action.payload.fireSelectionAnalytics?.({
					numSelectedCards: selectedCardIds.includes(cardId)
						? selectedCardIds.length - 1
						: selectedCardIds.length + 1,
					issue_ids: selectedCardIds.includes(cardId)
						? selectedCardIds.filter((id: CardId) => id !== cardId)
						: [...selectedCardIds, cardId],
				});

				return Observable.from(actions);
			}

			return Observable.empty<never>();
		});
